import { useState, useCallback } from 'react';

export interface ErrorState {
  error: string | Error | null;
  hasError: boolean;
}

export interface ErrorHandler {
  error: string | Error | null;
  hasError: boolean;
  setError: (error: string | Error | null) => void;
  clearError: () => void;
  handleAsyncError: <T>(asyncFn: () => Promise<T>) => Promise<T | null>;
}

/**
 * Hook personnalisé pour gérer les erreurs de manière centralisée
 */
export const useErrorHandler = (): ErrorHandler => {
  const [error, setError] = useState<string | Error | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleAsyncError = useCallback(async <T>(
    asyncFn: () => Promise<T>
  ): Promise<T | null> => {
    try {
      clearError();
      return await asyncFn();
    } catch (err: any) {
      console.error('Erreur capturée par useErrorHandler:', err);
      
      // Extraire le message d'erreur approprié
      let errorMessage: string;
      
      if (err.response?.data) {
        // Erreur de l'API
        const apiError = err.response.data;
        
        if (typeof apiError === 'string') {
          errorMessage = apiError;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        } else if (apiError.error) {
          errorMessage = apiError.error;
        } else if (typeof apiError === 'object') {
          // Erreurs de validation (format Django)
          const validationErrors = Object.entries(apiError)
            .map(([field, errors]) => {
              if (Array.isArray(errors)) {
                return `${field}: ${errors.join(', ')}`;
              }
              return `${field}: ${errors}`;
            })
            .join('; ');
          errorMessage = validationErrors || 'Erreur de validation';
        } else {
          errorMessage = 'Erreur du serveur';
        }
      } else if (err.message) {
        errorMessage = err.message;
      } else {
        errorMessage = 'Une erreur inattendue s\'est produite';
      }
      
      setError(errorMessage);
      return null;
    }
  }, [clearError]);

  return {
    error,
    hasError: error !== null,
    setError,
    clearError,
    handleAsyncError
  };
};
